import { useCallback, useEffect, useRef, useState } from 'react';
import { isValidAttachment } from '~/utils/attachments.client';

interface UseDragDropOptions {
  allowedExtensions?: Set<string>;
}

export function useDragDrop(
  onDrop?: (file: File) => Promise<void> | void,
  { allowedExtensions }: UseDragDropOptions = {},
) {
  const [isDragging, updateIsDragging] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>();

  const setIsDragging = useCallback((isDragging: boolean) => {
    updateIsDragging(isDragging);
    clearTimeout(timerRef.current);

    if (isDragging) {
      timerRef.current = setTimeout(() => {
        updateIsDragging(false);
      }, 3000);
    }
  }, []);

  useEffect(() => {
    const handleDragEnter = (event: DragEvent) => {
      event.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (event: DragEvent) => {
      event.preventDefault();

      if (
        event.clientX <= 0 ||
        event.clientX >= window.innerWidth ||
        event.clientY <= 0 ||
        event.clientY >= window.innerHeight
      ) {
        setIsDragging(false);
      }
    };

    const handleDragOver = (event: DragEvent) => {
      event.preventDefault();
      setIsDragging(true);
    };

    const handleDrop = (event: DragEvent) => {
      event.preventDefault();
      setIsDragging(false);
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);

      clearTimeout(timerRef.current);
    };
  }, []);

  const handleDrop = useCallback(
    async (event: React.DragEvent<HTMLDivElement>) => {
      setIsDragging(false);

      const { items } = event.dataTransfer;

      event.preventDefault();

      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile();

          const isDirectory = getAsEntry(items[i] as DataTransferItem)?.isDirectory;

          if (!isValidAttachment(file, { isDirectory, allowedExtensions })) {
            continue;
          }

          if (file) {
            await onDrop?.(file);
          }
        }
      }
    },
    [onDrop],
  );

  return { isDragging, handleDrop };
}

interface DataTransferItem {
  getAsEntry?: () => FileSystemEntry;
  webkitGetAsEntry?: () => FileSystemEntry;
}

function getAsEntry(item: DataTransferItem) {
  if (item.getAsEntry) {
    return item.getAsEntry();
  }

  if (item.webkitGetAsEntry) {
    return item.webkitGetAsEntry();
  }

  return undefined;
}
